import React, { useEffect, useState } from 'react';
import { ApiDocumentType, getDocumentTypes, addDocumentType, updateDocumentType, deleteDocumentType, getUserGroups, ApiUserGroup, getDocumentTypePermissions, ApiPermission, addPermission, deletePermission } from '@api';
import { ApiPermissionTemplate, getPermissionTemplates, createPermissionTemplate } from '@api/permissionTemplates';

const OBJECTTYPE_DOCUMENTTYPE = 2; // backend enum
interface DtForm { documentTypeName: string; documentTypeDescription: string; documentTypeExtension: string; documentTypeOCR: boolean; }
const emptyDt: DtForm = { documentTypeName: '', documentTypeDescription: '', documentTypeExtension: '', documentTypeOCR: false };

interface PermState { [groupId: number]: { full: boolean; templateId?: number; permissionId?: string; }; }

export default function DocumentTypeAdmin() {
  const [docTypes, setDocTypes] = useState<ApiDocumentType[]>([]);
  const [groups, setGroups] = useState<ApiUserGroup[]>([]);
  const [templates, setTemplates] = useState<ApiPermissionTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<DtForm>(emptyDt);
  const [permState, setPermState] = useState<PermState>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { init(); }, []);

  async function init() {
    setLoading(true); setError(null);
    try { const [dts, gs, tmpls] = await Promise.all([getDocumentTypes(), getUserGroups(), getPermissionTemplates()]); setDocTypes(dts); setGroups(gs); setTemplates(tmpls); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  async function loadPermissions(id: number) {
    try {
      const perms = await getDocumentTypePermissions(id);
      const map: PermState = {};
      perms.forEach(p => {
        const tmpl = templates.find(t => t.id === p.permissionTemplateId);
        const full = !!(tmpl && (tmpl.fullControl ?? (tmpl as any).FullControl));
        map[p.groupId] = { full, templateId: p.permissionTemplateId, permissionId: p.id };
      });
      groups.forEach(g => { if (!map[g.id]) map[g.id] = { full: false }; });
      setPermState(map);
    } catch { setPermState({}); }
  }

  function handleSelect(id: number) {
    setSelectedId(id); setEditing(false);
    const dt = docTypes.find(d => d.id === id);
    setForm({
      documentTypeName: dt?.documentTypeName || '',
      documentTypeDescription: dt?.documentTypeDescription || '',
      documentTypeExtension: dt?.documentTypeExtension || '',
      documentTypeOCR: !!(dt?.documentTypeOCR ?? (dt as any)?.DocumentTypeOCR)
    });
    loadPermissions(id);
  }
  function handleAdd() { setSelectedId(null); setEditing(true); setForm(emptyDt); setPermState({}); }
  function handleEdit() { if (selectedId != null) setEditing(true); }

  async function handleDelete() {
    if (selectedId == null) return; if (!window.confirm('Delete this document type?')) return;
    setLoading(true); setError(null);
    try { await deleteDocumentType(selectedId); await init(); setSelectedId(null); } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  async function handleSave() {
    setLoading(true); setError(null);
    try {
      const payload = {
        documentTypeName: form.documentTypeName,
        documentTypeDescription: form.documentTypeDescription,
        documentTypeExtension: form.documentTypeExtension,
        documentTypeOCR: !!form.documentTypeOCR
      } as Omit<ApiDocumentType, 'id'>;
      let id = selectedId;
      if (id == null) { const nd = await addDocumentType(payload); id = nd.id; }
      else { await updateDocumentType(id, payload); }
      await init(); setSelectedId(id!); setEditing(false); await loadPermissions(id!);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  function onFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value, type, checked } = e.target;
    setForm((p: DtForm) => ({
      ...p,
      [name]: type === 'checkbox' ? checked : value
    }));
  }
  function toggleFull(groupId: number) { setPermState(prev => { const clone = { ...prev }; if (!clone[groupId]) clone[groupId] = { full: false }; clone[groupId].full = !clone[groupId].full; return clone; }); }

  async function ensureFullTemplate(): Promise<ApiPermissionTemplate> {
    const fullExisting = templates.find(t => (t.fullControl ?? (t as any).FullControl) === true);
    if (fullExisting) return fullExisting;
    const tmpl = await createPermissionTemplate({ fullControl: true, canRead: true, canWrite: true, canModify: true, canDelete: true, permissionLabel: 'Full Control' });
    setTemplates((prev: ApiPermissionTemplate[]) => [...prev, tmpl]);
    return tmpl;
  }

  async function savePermissions() {
    if (selectedId == null) return;
    setLoading(true); setError(null);
    try {
      const currentPerms = await getDocumentTypePermissions(selectedId);
      for (const g of groups) {
        const st = permState[g.id]; const existing = currentPerms.find(p => p.groupId === g.id && p.objectId === selectedId && p.objectTypeId === OBJECTTYPE_DOCUMENTTYPE);
        if (!st || !st.full) { if (existing) await deletePermission(existing.id); continue; }
        const tmpl = await ensureFullTemplate();
        if (!existing) {
          await addPermission({ groupId: g.id, objectId: selectedId, objectTypeId: OBJECTTYPE_DOCUMENTTYPE, permissionTemplateId: tmpl.id });
        } else if (existing.permissionTemplateId !== tmpl.id) {
          await deletePermission(existing.id);
          await addPermission({ groupId: g.id, objectId: selectedId, objectTypeId: OBJECTTYPE_DOCUMENTTYPE, permissionTemplateId: tmpl.id });
        }
      }
      await loadPermissions(selectedId);
    } catch (e: any) { setError(e.message); } finally { setLoading(false); }
  }

  return (
      <div className="adm-shell">
      <div className="adm-master">
        <div className="adm-master-head">
          <h3>Document Types</h3>
          <button onClick={handleAdd} title='Add document type' className="adm-add-btn">
            <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><line x1='12' y1='5' x2='12' y2='19' /><line x1='5' y1='12' x2='19' y2='12' /></svg>
          </button>
        </div>
        {loading && <div className="adm-status">Loading...</div>}
        {error && <div className="adm-status adm-status--error">{error}</div>}
        <div className="adm-master-scroll">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ width: 90 }}>Ext</th>
                <th className="adm-center" style={{ width: 60 }}>OCR</th>
              </tr>
            </thead>
            <tbody>
              {docTypes.map(dt => { const sel = dt.id === selectedId; return (
                <tr key={dt.id} onClick={() => handleSelect(dt.id)} className={`adm-row${sel ? ' is-selected' : ''}`}>
                  <td>{dt.documentTypeName}</td>
                  <td>{dt.documentTypeExtension || ''}</td>
                  <td className="adm-center">{(dt.documentTypeOCR ?? (dt as any).DocumentTypeOCR) ? '✓' : ''}</td>
                </tr> ); })}
              {docTypes.length === 0 && !loading && (<tr><td colSpan={3} className="adm-empty">No document types</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      <div className="adm-detail">
        {editing ? (
          <div style={{ maxWidth: 700 }}>
            <h3 style={{ marginTop: 0 }}>{selectedId == null ? 'Add Document Type' : 'Edit Document Type'}</h3>
            <form onSubmit={e => { e.preventDefault(); handleSave(); }} className="adm-card adm-form-grid">
              <label className="adm-label">Name</label>
              <input name='documentTypeName' value={form.documentTypeName} onChange={onFormChange} required className="adm-input" />
              <label className="adm-label">Description</label>
              <input name='documentTypeDescription' value={form.documentTypeDescription} onChange={onFormChange} className="adm-input" />
              <label className="adm-label">Extension</label>
              <input name='documentTypeExtension' value={form.documentTypeExtension} onChange={onFormChange} required className="adm-input" />
              <label className="adm-label">OCR</label>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input name='documentTypeOCR' type='checkbox' checked={!!form.documentTypeOCR} onChange={onFormChange} />
              </div>
              <div className="adm-actions" style={{ gridColumn: '1 / span 2' }}>
                <button type='submit' className="adm-btn adm-btn-primary" disabled={loading}>Save</button>
                <button type='button' className="adm-btn adm-btn-secondary" onClick={() => setEditing(false)} disabled={loading}>Cancel</button>
              </div>
            </form>
          </div>
        ) : selectedId != null ? (
          <div style={{ maxWidth: 1400 }}>
            <div className="adm-toolbar">
              <h3 style={{ margin: 0 }}>Document Type Details</h3>
              <div className="adm-actions-group">
                <button onClick={handleEdit} className="adm-btn adm-btn-secondary" disabled={loading}>Edit</button>
                <button onClick={handleDelete} className="adm-btn adm-btn-danger" disabled={loading}>Delete</button>
              </div>
            </div>
            <div className="adm-card adm-card--sm" style={{ marginBottom: 20, display: 'grid', gridTemplateColumns: '160px 1fr', rowGap: 6 }}>
              <div className="adm-field-label">Name:</div><div>{form.documentTypeName}</div>
              <div className="adm-field-label">Description:</div><div>{form.documentTypeDescription}</div>
              <div className="adm-field-label">Extension:</div><div>{form.documentTypeExtension}</div>
              <div className="adm-field-label">OCR:</div><div>{form.documentTypeOCR ? 'Yes' : 'No'}</div>
            </div>

            <div className="adm-card adm-card--sm">
              <h4 style={{ margin: '0 0 12px 0' }}>Permissions (Full Control only)</h4>
              <div className="adm-scroll-x">
                <table className="adm-table adm-table--compact adm-table--auto" style={{ minWidth: 600 }}>
                  <thead>
                    <tr>
                      <th>Group</th>
                      <th className="adm-center">Full</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map(g => { const st = permState[g.id] || { full: false }; return (
                      <tr key={g.id}>
                        <td>{g.groupName}</td>
                        <td className="adm-center"><input type='checkbox' checked={st.full} onChange={() => toggleFull(g.id)} /></td>
                      </tr> ); })}
                  </tbody>
                </table>
              </div>
              <div className="adm-actions">
                <button onClick={savePermissions} className="adm-btn adm-btn-primary" disabled={loading}>Save Permissions</button>
                <button onClick={() => loadPermissions(selectedId!)} className="adm-btn adm-btn-secondary" disabled={loading}>Reset</button>
              </div>
            </div>
          </div>
        ) : (
          <div className="adm-placeholder">Select a document type or click the + icon to add a new one.</div>
        )}
      </div>
    </div>
  );
}
